/**
 * The pieces a customisable-dashboard card is built from.
 *
 * Every card on the board has the same skeleton — a header row, a headline figure, a chart or
 * ring in whatever height is left, and a one-line footer saying what window the figures cover —
 * and, when expanded, a details panel under all of that. These parts are that skeleton, so a
 * card definition is only its figures and its words.
 *
 * ── Filters ──
 *
 * While a card is expanded, the figures in its summary are also its list's filters: a legend
 * row, the headline, a chip. Picking one sets the card's `filter` (one at a time; picking it
 * again clears it), and `DetailsPanel` keeps only the items whose `groups` include that key.
 * Shrunk, the same figures are plain text — there is no list to filter.
 *
 * Each part reads the card's own state (size, captions on/off) from `BoardCardContext`, which
 * the board provides around every card. That is what lets a chart step down to a fixed strip
 * when its card expands without every card having to thread `expanded` through by hand.
 *
 * ── Colour ──
 *
 * The theme's colour tokens are bare `var()`s, and Tailwind emits nothing for an opacity
 * modifier on one (`bg-brand/10` compiles to no rule at all). Every tint here is therefore a
 * `color-mix()` in an inline style rather than a class.
 */
import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import { ArrowRightIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { BoardRange, BoardSize, BoardView } from './boardLayout';
import { AxisRow, Gauge, MiniBars, bucketLabel, hasSeries, seriesMax, type Series } from '../MiniCharts';

// ─── Tokens ──────────────────────────────────────────────────────────────────

export const TONE = {
  crit: 'var(--destructive)',
  warn: 'var(--warning)',
  info: 'var(--info)',
  ok: 'var(--success)',
  muted: 'var(--muted-foreground)',
  brand: 'var(--brand)',
} as const;

/** `c` at `pct`% over transparent — the soft fill behind a pill, a chip or an emphasised row. */
export const tint = (c: string, pct: number) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

/** Keyboard focus ring for every control on the board (the design's 2px brand outline). */
export const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1';

/** The recessed surface inside a card (panel bars, severity boxes, icon tiles). */
export const SUNK = 'color-mix(in srgb, var(--muted) 45%, var(--card))';

const nf = new Intl.NumberFormat();
export const fmt = (v: number | null | undefined) => nf.format(Number(v || 0));

/** "Today, since midnight" / "Last 24 hours" — from the window the NODE says it used. */
export function windowCaption(label?: string | null): string {
  if (label === '24h') return 'Last 24 hours';
  if (label === '7d') return 'Last 7 days';
  return 'Today, since midnight';
}

/** The word that follows a figure: "17 sightings today" / "… in 24 h" / "… in 7 days". */
export function windowSuffix(label?: string | null): string {
  if (label === '24h') return 'in 24 h';
  if (label === '7d') return 'in 7 days';
  return 'today';
}

/** "per hour" / "per 6 h" — what one bar of a series is, from the series' own step. */
export function perBucket(series?: Series | null): string {
  const step = series?.step || 3600;
  if (step === 900) return 'per 15 min';
  if (step === 3600) return 'per hour';
  if (step === 86400) return 'per day';
  return `per ${Math.round(step / 3600)} h`;
}

// ─── Card context ────────────────────────────────────────────────────────────

export interface BoardCardState {
  id: string;
  size: BoardSize;
  view: BoardView;
  showMeta: boolean;
  range: BoardRange;
  editing: boolean;
  /**
   * Expanded with the details BESIDE the summary rather than under it — every board except a
   * narrow one. `summaryWidth` is then a shrunk card's width in px, so the summary half of an
   * expanded card is the shrunk card exactly, not a reflowed copy of it.
   */
  split: boolean;
  summaryWidth?: number;
  /** The list filter picked from the summary, or null. Only ever set while expanded. */
  filter: BoardFilter | null;
  setFilter: (filter: BoardFilter | null) => void;
}

export interface BoardFilter {
  key: string;
  label: string;
}

export const BoardCardContext = createContext<BoardCardState>({
  id: '',
  size: 'shrink',
  view: 'list',
  showMeta: true,
  range: 'today',
  editing: false,
  split: false,
  filter: null,
  setFilter: () => {},
});

export const useBoardCard = () => useContext(BoardCardContext);

/**
 * The summary's side of filtering. `filterable` is false on a shrunk card (no list to filter);
 * `pick` toggles: the active key again clears it.
 */
export function useCardFilter() {
  const { size, split, filter, setFilter } = useBoardCard();
  const filterable = size === 'expand' || split;
  const pick = (key: string, label: string) =>
    setFilter(filter?.key === key ? null : { key, label });
  return { filterable, active: filterable ? filter?.key ?? null : null, pick };
}

// ─── Header ──────────────────────────────────────────────────────────────────

export function IconTile({ icon: Icon, size = 32 }: { icon: ComponentType<{ className?: string }>; size?: number }) {
  return (
    <span
      className="flex flex-none items-center justify-center rounded-[9px] text-muted-foreground"
      style={{ width: size, height: size, backgroundColor: SUNK }}
      aria-hidden
    >
      <Icon className="h-[17px] w-[17px]" />
    </span>
  );
}

/** The small red count beside a title — "770 open", "1 seen". */
export function Chip({ children, tone = TONE.crit, onClick, title }: {
  children: ReactNode;
  tone?: string;
  onClick?: () => void;
  title?: string;
}) {
  const cls = 'flex-none whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tabular-nums';
  const style = { color: tone, backgroundColor: tint(tone, 12) };
  return onClick ? (
    <button type="button" onClick={onClick} title={title} className={`${cls} hover:underline`} style={style}>
      {children}
    </button>
  ) : (
    <span className={cls} style={style} title={title}>{children}</span>
  );
}

export function CardHead({
  icon,
  title,
  chip,
  caption,
  actions,
  onOpen,
  openLabel,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  chip?: ReactNode;
  /** A muted line after the title — the System card's "This machine — live load…". */
  caption?: ReactNode;
  /** Controls between the title and the open arrow (the alert timeline's severity filter). */
  actions?: ReactNode;
  /** Where the arrow goes. Omit and there is no arrow — a button that goes nowhere is worse. */
  onOpen?: () => void;
  openLabel?: string;
}) {
  return (
    <div className="flex min-w-0 flex-none items-center gap-3">
      <IconTile icon={icon} />
      <h3 className="min-w-0 truncate text-[13px] font-semibold uppercase tracking-[0.07em] text-foreground" title={title}>
        {title}
      </h3>
      {chip}
      {caption && <span className="hidden min-w-0 truncate text-[12px] text-muted-foreground lg:block">{caption}</span>}
      <span className="flex-1" />
      {actions}
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          title={openLabel || `Open ${title}`}
          aria-label={openLabel || `Open ${title}`}
          className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${FOCUS}`}
        >
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ─── Figures ─────────────────────────────────────────────────────────────────

/** The headline number and what it counts. The unit is not decoration — see AiAnalyticsCards. */
export function Hero({ value, unit, tone, hint, filterKey }: {
  value: ReactNode;
  unit: ReactNode;
  tone?: string;
  hint?: string;
  /** Expanded, the headline is a filter: clicking it shows only the items it counts. */
  filterKey?: string;
}) {
  const f = useCardFilter();
  if (filterKey && f.filterable) {
    const on = f.active === filterKey;
    const label = typeof unit === 'string' ? unit : filterKey;
    return (
      <button
        type="button"
        aria-pressed={on}
        onClick={() => f.pick(filterKey, label)}
        title={on ? 'Show everything' : `Show only: ${label}`}
        className={`-mx-1.5 flex flex-none items-baseline gap-2 self-start rounded-lg px-1.5 py-0.5 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${FOCUS}`}
        style={on ? { boxShadow: `inset 0 0 0 1.5px ${tone || 'var(--brand)'}` } : undefined}
      >
        <span
          className="text-[38px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-foreground"
          style={tone ? { color: tone } : undefined}
        >
          {value}
        </span>
        <span className="min-w-0 truncate text-[14px] text-muted-foreground">{unit}</span>
      </button>
    );
  }
  return (
    <div className="flex flex-none items-baseline gap-2">
      <span
        className="text-[38px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-foreground"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </span>
      <span
        className={`min-w-0 truncate text-[14px] text-muted-foreground ${hint ? 'decoration-dotted underline-offset-2 hover:underline' : ''}`}
        title={hint}
      >
        {unit}
      </span>
    </div>
  );
}

/**
 * The part of a shrunk card under the headline: a top rule and every pixel that is left.
 * Expanded, it keeps its natural height so the details panel gets the rest.
 */
export function CardSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const { size } = useBoardCard();
  return (
    <div className={`flex min-h-0 flex-col border-t border-border pt-2.5 ${size === 'expand' ? 'flex-none' : 'flex-1'} ${className}`}>
      {children}
    </div>
  );
}

/**
 * A series as bars, with what the bars measure above and where they sit in time below.
 *
 * Shrunk, the bars take the card's spare height (54–140 px); expanded they are a fixed strip, so
 * the summary stays short and the list beneath it gets the room. The caption line is part of
 * the card's "captions" setting; the time axis is not — without it the bars cannot be placed.
 */
export function BarsPlot({
  series,
  color,
  caption,
  scale,
  emphasis = 'peak',
  left,
  unit,
}: {
  series: Series;
  color: string;
  caption: ReactNode;
  scale?: ReactNode;
  emphasis?: 'peak' | 'last' | 'none';
  /** First axis label. Defaults to the first bucket's clock reading. */
  left?: ReactNode;
  unit?: string;
}) {
  const { size, showMeta } = useBoardCard();
  const expanded = size === 'expand';
  return (
    <div className={`flex min-h-0 flex-col ${expanded ? 'flex-none' : 'flex-1'}`}>
      {showMeta && (
        <div className="mb-1.5 flex flex-none items-baseline justify-between gap-2 text-[11px] leading-none">
          <span className="min-w-0 truncate text-muted-foreground">{caption}</span>
          <span className="flex-none font-bold tabular-nums text-foreground">
            {scale ?? `max ${fmt(seriesMax(series))}`}
          </span>
        </div>
      )}
      <div className={expanded ? 'h-[64px] flex-none' : 'min-h-[54px] max-h-[140px] flex-1'}>
        <MiniBars series={series} color={color} emphasis={emphasis} unit={unit} />
      </div>
      <AxisRow left={left ?? bucketLabel(series, 0)} right="now" />
    </div>
  );
}

/** A series, or the honest sentence for an empty one. */
export function BarsOrQuiet(props: Parameters<typeof BarsPlot>[0] & { total: number; quiet: ReactNode }) {
  const { total, quiet, ...rest } = props;
  if (total > 0 && hasSeries(rest.series)) return <BarsPlot {...rest} />;
  return <Quiet>{quiet}</Quiet>;
}

/**
 * A ring and its legend. 96 px shrunk, 74 px expanded — see `Gauge`. Expanded, each legend row
 * filters the card's list by its key (a row with `pickable: false` does not).
 */
export function RingStat(props: Omit<Parameters<typeof Gauge>[0], 'size' | 'onPick' | 'active'>) {
  const { size } = useBoardCard();
  const f = useCardFilter();
  return (
    <div className={`flex min-h-0 items-center ${size === 'expand' ? 'flex-none' : 'flex-1'}`}>
      <div className="w-full">
        <Gauge
          {...props}
          size={size === 'expand' ? 74 : 96}
          onPick={f.filterable ? f.pick : undefined}
          active={f.active}
        />
      </div>
    </div>
  );
}

/**
 * A row of counted filters for a card whose summary has no breakdown of its own — the watch
 * lists' categories, a detector's event types. Expanded only; a shrunk card stays as it was.
 */
export function FilterChips({ chips }: {
  chips: Array<{ key: string; label: string; count: number; color?: string }>;
}) {
  const f = useCardFilter();
  if (!f.filterable || chips.length < 2) return null;
  return (
    <div className="-mx-1 flex flex-none gap-1.5 overflow-x-auto px-1 py-0.5" role="group" aria-label="Filter the list">
      {chips.map((c) => {
        const on = f.active === c.key;
        const color = c.color || TONE.brand;
        return (
          <button
            key={c.key}
            type="button"
            aria-pressed={on}
            onClick={() => f.pick(c.key, c.label)}
            title={on ? 'Show everything' : `Show only: ${c.label}`}
            className={`flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-[3px] text-[11px] font-medium transition-colors ${FOCUS} ${on ? 'text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
            style={on ? { borderColor: color, backgroundColor: tint(color, 12) } : { backgroundColor: SUNK }}
          >
            <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ backgroundColor: color }} aria-hidden />
            {c.label}
            <span className="font-semibold tabular-nums text-foreground">{fmt(c.count)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Nothing to show, said on purpose — a tick, because nothing happening is the good outcome. */
export function Quiet({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  const { size } = useBoardCard();
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border border-border px-3.5 py-3 ${size === 'expand' ? 'flex-none' : 'my-auto'}`}
      style={{ backgroundColor: SUNK }}
    >
      <CheckCircleIcon className="h-5 w-5 flex-none" style={{ color: TONE.ok }} aria-hidden />
      <div className="min-w-0">
        <p className="text-[13px] font-semibold leading-tight text-foreground">{children}</p>
        {sub && <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

/** The meta line pinned to the foot of a card: what window, what scope. Follows "captions". */
export function CardFoot({ children }: { children: ReactNode }) {
  const { showMeta } = useBoardCard();
  if (!showMeta || !children) return null;
  return (
    <p className="mt-auto flex flex-none items-center gap-1.5 border-t border-border pt-2 text-[11px] text-muted-foreground">
      <ClockIcon className="h-[13px] w-[13px] flex-none" aria-hidden />
      <span className="min-w-0 truncate" title={typeof children === 'string' ? children : undefined}>{children}</span>
    </p>
  );
}

/** A labelled load meter — CPU, memory, disk. Colour follows how close to full it is. */
export function Meter({ label, percent, sub }: { label: string; percent: number | null | undefined; sub?: string }) {
  const known = percent != null && Number.isFinite(Number(percent));
  const pct = known ? Math.max(0, Math.min(100, Number(percent))) : 0;
  const color = pct >= 85 ? TONE.crit : pct >= 70 ? 'var(--brand)' : pct >= 50 ? TONE.warn : TONE.ok;
  return (
    <div className="min-w-0" title={sub}>
      <div className="flex items-baseline justify-between gap-2 text-[11px] leading-none">
        <span className="truncate text-muted-foreground">{label}</span>
        <span className="flex-none font-bold tabular-nums text-foreground">{known ? `${Math.round(pct)}%` : '—'}</span>
      </div>
      <div className="mt-1.5 h-[5px] overflow-hidden rounded-full" style={{ backgroundColor: tint(TONE.muted, 20) }}>
        {known && <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />}
      </div>
    </div>
  );
}

/** Loading placeholder for a card body. */
export function CardSkeleton() {
  return (
    <div className="flex flex-1 animate-pulse flex-col gap-3 pt-1" aria-hidden>
      <div className="h-9 w-24 rounded-md bg-muted" />
      <div className="flex-1 rounded-lg bg-muted" />
    </div>
  );
}

/**
 * A card's body: the summary block, then — expanded — the details panel taking whatever is
 * left. The summary scrolls rather than overflows when an edit toolbar squeezes a shrunk card.
 *
 * Split (expanded on anything but a narrow board), the summary keeps a shrunk card's width and
 * the details fill the rest of the row beside it. The summary's parts are given a SHRINK context
 * so its chart, ring and empty state lay out exactly as on the shrunk card — expanding a card
 * widens it, it does not change what the summary looks like.
 */
export function BoardCardBody({
  head,
  children,
  details,
}: {
  head: ReactNode;
  children: ReactNode;
  /** Rendered only while the card is expanded. */
  details?: ReactNode;
}) {
  const card = useBoardCard();
  const expanded = card.size === 'expand' && !!details;
  if (expanded && card.split) {
    return (
      <div className="flex min-h-0 flex-1 flex-row">
        <BoardCardContext.Provider value={{ ...card, size: 'shrink' }}>
          <div
            className="flex min-h-0 flex-none flex-col gap-2.5 overflow-y-auto px-4 pb-3 pt-3.5"
            style={{ width: card.summaryWidth || 320 }}
          >
            {head}
            {children}
          </div>
        </BoardCardContext.Provider>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col border-l border-border">{details}</div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={`flex min-h-0 flex-col gap-2.5 px-4 pb-3 pt-3.5 ${expanded ? 'flex-none' : 'flex-1 overflow-y-auto'}`}
      >
        {head}
        {children}
      </div>
      {expanded && details}
    </div>
  );
}
